import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import supabase from '../supabase';

const Registry = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRegistry = async () => {
      // ⚡️ STRATEGY: Filter for quality. Only show companies with data.
      const { data, error } = await supabase
        .from('businesses')
        .select('www, title, category, isverif, updated_at, reviews')
        .gt('reviews', 0) // Only show if they have at least 1 review
        .order('isverif', { ascending: false })
        .order('updated_at', { ascending: false });

      if (!error) setBusinesses(data);
      setLoading(false);
    };
    fetchRegistry();
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa] text-black font-sans selection:bg-black selection:text-white">
<Helmet>
  <title>Public Registry | Sitemanifest Index</title>
  <meta name="description" content="The official index of verified business manifests." />
  
  {/* CHANGE THIS LINE BELOW */}
  <meta name="robots" content="index, follow" />
  
  <meta name="googlebot" content="index, follow, noarchive" />
  <link rel="canonical" href="https://sitemanifest.com/registry" />
  
</Helmet>
      <main className="max-w-5xl mx-auto px-6 pt-32 pb-20">
        {/* Header Section */}
        <div className="flex justify-between items-end mb-16 border-b border-black/5 pb-8">
          <div>
            <h1 className="text-[10px] uppercase tracking-[0.4em] text-gray-400 mb-2">Verified Repository</h1>
            <h2 className="text-3xl font-light tracking-tighter uppercase">Public Registry</h2>
          </div>
          <div className="text-[10px] text-gray-300 uppercase tracking-widest font-mono">
            {businesses.length} Verified Entries
          </div>
        </div>

        {/* The Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          {/* Dynamic Business Cards */}
          {!loading && businesses.map((biz) => (
            <Link 
              key={biz.www} 
              to={`/company/${biz.www}`}
              className="group relative bg-white border border-gray-100 p-8 hover:border-black transition-all duration-500 flex flex-col justify-between h-[280px]"
            >
              <div>
                <div className="flex justify-between items-start mb-8">
                  <span className="text-[9px] uppercase tracking-widest text-gray-400 bg-gray-50 px-2 py-1">
                    {biz.category || 'Manifest'}
                  </span>
                  {biz.isverif && (
                    <div className="h-2 w-2 bg-green-500 rounded-full shadow-[0_0_8px_rgba(34,197,94,0.4)]"></div>
                  )}
                </div>
                <h3 className="text-xl font-light lowercase group-hover:translate-x-1 transition-transform duration-300 truncate">
                  {biz.www}
                </h3>
                <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-1">
                  {biz.title || 'Official Entry'}
                </p>
              </div>

              <div className="flex justify-between items-center pt-8 border-t border-gray-50 mt-auto">
                <div className="flex flex-col">
                    <span className="text-[8px] uppercase tracking-widest text-gray-300 font-mono">
                      Audit: {new Date(biz.updated_at).toLocaleDateString('en-GB')}
                    </span>
                    <span className="text-[9px] text-black font-bold mt-1">
                        {biz.reviews_count} Verified Points
                    </span>
                </div>
                <span className="text-[10px] text-black group-hover:translate-x-2 transition-transform">—&gt;</span>
              </div>
            </Link>
          ))}

          {/* THE INVITATION STATE: Always visible to drive growth */}
          <Link 
            to="/signup" 
            className="group relative bg-black text-white p-8 hover:bg-[#111] transition-all duration-500 flex flex-col justify-center items-center h-[280px] text-center"
          >
            <h3 className="text-lg font-light uppercase tracking-widest mb-4">Missing a Manifest?</h3>
            <p className="text-[10px] text-gray-400 uppercase tracking-widest leading-relaxed mb-6 px-4">
              Unlisted domains carry higher risk. <br/> Secure your presence in the registry.
            </p>
            <span className="px-4 py-2 border border-gray-700 text-[9px] uppercase tracking-[0.2em] group-hover:border-white transition-colors">
              Register Business
            </span>
          </Link>

        </div>

        {/* Loading State for small DB */}
        {loading && (
            <div className="mt-10 text-[10px] uppercase tracking-[0.3em] text-gray-300 animate-pulse text-center w-full">
                Querying Protocol...
            </div>
        )}

        {/* SEO TRICK: The Mission Statement Section */}
        <section className="mt-32 pt-20 border-t border-gray-100 max-w-2xl">
          <h4 className="text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-6">Protocol Information</h4>
          <p className="text-sm text-gray-500 leading-relaxed font-light italic">
            "Sitemanifest is a high-integrity repository. Each entity listed in this registry has undergone a 
            protocol-level domain verification. We do not index noise; we only index truth. 
            By centralizing verified business manifests, we create a transparent layer for the 
            modern web where reputation is earned and data is immutable."
          </p>
          <div className="mt-8 flex gap-8">
            <div className="flex flex-col">
              <span className="text-xl font-light">01</span>
              <span className="text-[8px] uppercase tracking-widest text-gray-400">Verified Origin</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-light">02</span>
              <span className="text-[8px] uppercase tracking-widest text-gray-400">Zero-Noise Audit</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-light">03</span>
              <span className="text-[8px] uppercase tracking-widest text-gray-400">Domain Authority</span>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Registry;